import * as React from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, IconButton, Typography } from '@material-ui/core';
import { OverridableComponent } from '@material-ui/core/OverridableComponent';
import { SvgIconTypeMap } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Form, FormSpy, useFormState } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import AssignmentOutlinedIcon from '@material-ui/icons/AssignmentOutlined';
import TimerIcon from '@material-ui/icons/Timer';
import PlaylistAddCheckIcon from '@material-ui/icons/PlaylistAddCheck';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import GroupOutlinedIcon from '@material-ui/icons/GroupOutlined';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { ConfirmDialog } from '../../components/Dialogs/ConfirmDialog';
import { useTaskflowBuilder } from '../taskflowBuilderContext';
import { TASKFLOW_LANE_LABEL, TaskflowTask } from '../types';
import { DetailsTab } from './DetailsTab';
import { DueDateTab } from './DueDateTab';
import { OnCompletionTab } from './OnCompletionTab';
import { AccessRuleTab } from './AccessRuleTab';

type ConfigureTaskTabKey = 'details' | 'due' | 'onCompletion' | 'access';

// ─── Colour tokens (matches MarketingRuleSetupDrawerForm) ────────────────────
const BLUE = '#1565c0';
const ORANGE = '#e65100';
const GREEN = '#2e7d32';
const PURPLE = '#6a1b9a';

const TABS: { key: ConfigureTaskTabKey; label: string; description: string; icon: OverridableComponent<SvgIconTypeMap>; color: string; bgColor: string }[] = [
    { key: 'details', label: 'Details', description: 'What this task is and who does it', icon: AssignmentOutlinedIcon, color: BLUE, bgColor: '#e3f2fd' },
    { key: 'due', label: 'Due date', description: 'When it falls due', icon: TimerIcon, color: ORANGE, bgColor: '#fff3e0' },
    { key: 'onCompletion', label: 'On completion', description: 'What happens next, automatically', icon: PlaylistAddCheckIcon, color: GREEN, bgColor: '#e8f5e9' },
    { key: 'access', label: 'Access rule', description: "Who's excluded from this task", icon: LockOutlinedIcon, color: PURPLE, bgColor: '#f3e5f5' },
];

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        // ── Sticky header bar ────────────────────────────────────────────────
        dialogHeader: {
            background: BLUE,
            color: '#fff',
            padding: theme.spacing(1.75, 2.5),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
            borderBottom: '1px solid rgba(255,255,255,0.2)',
        },
        dialogHeaderContent: {
            minWidth: 0,
        },
        dialogHeaderEyebrow: {
            fontSize: 11,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            fontWeight: 500,
            color: '#fff',
            opacity: 0.9,
            marginBottom: 2,
        },
        dialogHeaderTitle: {
            fontWeight: 600,
            fontSize: 17,
            color: '#fff',
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        dialogHeaderBadge: {
            border: '1px solid rgba(255,255,255,0.35)',
            borderRadius: 999,
            padding: theme.spacing(0.4, 1.25),
            fontSize: 11,
            fontWeight: 500,
            color: '#fff',
            background: 'rgba(255,255,255,0.1)',
            whiteSpace: 'nowrap',
            flexShrink: 0,
        },

        wrap: {
            display: 'flex',
            flex: 1,
            minHeight: 0,
        },
        side: {
            width: 230,
            flexShrink: 0,
            borderRight: `1px solid ${theme.palette.divider}`,
            backgroundColor: '#fff',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
        },
        sideInfo: {
            padding: theme.spacing(1.5, 1.5, 0, 1.5),
        },
        assignCard: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.25),
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 10,
            padding: theme.spacing(1.1, 1.25),
            backgroundColor: theme.palette.grey[50],
            marginBottom: theme.spacing(1.25),
        },
        assignIconWrap: {
            width: 32,
            height: 32,
            borderRadius: 8,
            backgroundColor: '#e3f2fd',
            color: BLUE,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
        },
        assignText: {
            minWidth: 0,
        },
        assignValue: {
            fontSize: 13.5,
            fontWeight: 700,
            color: theme.palette.text.primary,
            lineHeight: 1.3,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        assignMode: {
            fontSize: 11,
            color: theme.palette.text.secondary,
            marginTop: 1,
        },
        gateFlag: {
            fontSize: 10.5,
            fontWeight: 700,
            color: '#a15f04',
            backgroundColor: '#fff6e9',
            border: '1px solid #e18713',
            padding: theme.spacing(0.75, 1.1),
            borderRadius: 8,
            lineHeight: 1.4,
        },
        tabList: {
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(0.5),
        },
        tabRailLine: {
            position: 'absolute',
            left: 27,
            top: 28,
            bottom: 24,
            width: 2,
            backgroundColor: theme.palette.grey[200],
            zIndex: 0,
        },
        tabButton: {
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'flex-start',
            gap: theme.spacing(1.25),
            width: '100%',
            textAlign: 'left',
            border: 'none',
            background: 'none',
            padding: theme.spacing(1.1, 1.5),
            cursor: 'pointer',
            borderLeft: '3px solid transparent',
            transition: 'background-color 0.12s, border-color 0.12s',
            '&:hover': {
                backgroundColor: theme.palette.grey[50],
            },
        },
        tabButtonActive: {
            borderLeft: `3px solid ${BLUE}`,
            backgroundColor: '#e3f2fd',
        },
        tabIconWrap: {
            position: 'relative',
            zIndex: 1,
            width: 30,
            height: 30,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
        },
        tabText: {
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            paddingTop: 2,
        },
        tabLabel: {
            fontSize: 13,
            fontWeight: 700,
            color: theme.palette.text.primary,
            lineHeight: 1.3,
        },
        tabLabelActive: {
            color: BLUE,
        },
        tabDescription: {
            fontSize: 11,
            color: theme.palette.text.secondary,
            lineHeight: 1.35,
            marginTop: 1,
        },
        sideNav: {
            marginTop: 'auto',
            borderTop: `1px solid ${theme.palette.divider}`,
            padding: theme.spacing(1, 1.5),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
        },
        sideNavLabel: {
            fontSize: 11.5,
            fontWeight: 700,
            color: theme.palette.text.secondary,
        },
        sideNavBtn: {
            padding: 5,
            color: theme.palette.text.secondary,
            '&:hover': {
                backgroundColor: theme.palette.grey[100],
                color: theme.palette.text.primary,
            },
        },
        main: {
            flex: 1,
            padding: theme.spacing(2.5),
            minWidth: 0,
            minHeight: 0,
            overflowY: 'auto',
            backgroundColor: '#f5f5f5',
        },

        // ── Sticky footer buttons ─────────────────────────────────────────────
        footer: {
            borderTop: '1px solid #e0e0e0',
            padding: theme.spacing(1.75, 2.5),
            background: '#fff',
        },
    })
);

const FormValuesSync: React.FC<{ onValuesChange: (values: TaskflowTask) => void }> = ({ onValuesChange }) => {
    const { values } = useFormState<TaskflowTask>({ subscription: { values: true } });

    React.useEffect(() => {
        onValuesChange(values);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values]);

    return null;
};

interface ConfigureTaskDialogProps {
    open: boolean;
    draftTask?: TaskflowTask | null;
    onClose: () => void;
}

export const ConfigureTaskDialog: React.FC<ConfigureTaskDialogProps> = ({ open, draftTask = null, onClose }) => {
    const classes = useStyles();
    const { activeTaskflow, activeTask, dispatch } = useTaskflowBuilder();
    const [activeTab, setActiveTab] = React.useState<ConfigureTaskTabKey>('details');
    const [isConfirmDiscardOpen, setIsConfirmDiscardOpen] = React.useState(false);
    const isNewTask = !!draftTask;
    const task = draftTask ?? activeTask;
    const initialValues = React.useMemo(() => task, [task?.id]);

    if (!activeTaskflow || !task || !initialValues) {
        return null;
    }

    const taskPosition = activeTaskflow.taskOrder.indexOf(task.id) + 1;
    const tabIndex = TABS.findIndex(tab => tab.key === activeTab);

    // Existing tasks live-sync every keystroke into the reducer (so other parts of the
    // wizard reflect edits immediately). A draft (not-yet-added) task is only committed
    // once, via ADD_TASK, when Save is clicked — otherwise it'd appear in the task list
    // before the user decided to keep it.
    const onExistingTaskValuesChange = (values: TaskflowTask) => {
        dispatch({ type: 'UPDATE_TASK', taskId: values.id, updates: values });
    };

    const goToPreviousTab = () => setActiveTab(TABS[Math.max(0, tabIndex - 1)].key);
    const goToNextTab = () => setActiveTab(TABS[Math.min(TABS.length - 1, tabIndex + 1)].key);

    const closeAndDiscard = () => {
        if (!isNewTask) {
            dispatch({ type: 'UPDATE_TASK', taskId: task.id, updates: initialValues });
        }
        setIsConfirmDiscardOpen(false);
        onClose();
    };

    return (
        <Form<TaskflowTask>
            onSubmit={onExistingTaskValuesChange}
            initialValues={initialValues}
            mutators={{ ...arrayMutators }}
            subscription={{ submitting: true, pristine: true }}
            render={({ form, pristine }) => {
                const requestClose = () => {
                    if (!pristine) {
                        setIsConfirmDiscardOpen(true);
                    } else {
                        onClose();
                    }
                };

                const saveAndClose = () => {
                    if (isNewTask) {
                        dispatch({ type: 'ADD_TASK', task: form.getState().values });
                    }
                    onClose();
                };

                return (
                    <>
                        <Dialog
                            open={open}
                            onClose={requestClose}
                            maxWidth="lg"
                            fullWidth={true}
                            PaperProps={{ style: { minHeight: 852 } }}
                        >
                            <Box className={classes.dialogHeader}>
                                <Box className={classes.dialogHeaderContent}>
                                    <Typography className={classes.dialogHeaderEyebrow}>
                                        {isNewTask ? 'New task' : `Task ${taskPosition} of ${activeTaskflow.taskOrder.length}`}
                                    </Typography>
                                    <Typography className={classes.dialogHeaderTitle}>{task.title}</Typography>
                                </Box>
                                <Typography className={classes.dialogHeaderBadge}>
                                    {TASKFLOW_LANE_LABEL[task.lane]}
                                </Typography>
                            </Box>

                            {!isNewTask && <FormValuesSync onValuesChange={onExistingTaskValuesChange} />}
                            <DialogContent dividers={true} style={{ padding: 0, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                                <Box className={classes.wrap}>
                                    <Box className={classes.side}>
                                        <Box className={classes.sideInfo}>
                                            <FormSpy<TaskflowTask> subscription={{ values: true }}>
                                                {({ values }) => {
                                                    const isResponsibility = values.assignMode === 'resp';
                                                    const AssignIcon = isResponsibility ? PersonOutlineIcon : GroupOutlinedIcon;
                                                    return (
                                                        <Box className={classes.assignCard}>
                                                            <Box className={classes.assignIconWrap}>
                                                                <AssignIcon fontSize="small" />
                                                            </Box>
                                                            <Box className={classes.assignText}>
                                                                <Typography className={classes.assignValue}>{values.assignValue?.label}</Typography>
                                                                <Typography className={classes.assignMode}>
                                                                    {isResponsibility ? 'Responsibility' : 'Team'}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    );
                                                }}
                                            </FormSpy>

                                            {task.isGate && (
                                                <Typography className={classes.gateFlag}>
                                                    ⬥ Branches on completion — needs an outcome value + conditional On Completion rules
                                                </Typography>
                                            )}
                                        </Box>

                                        <Box className={classes.tabList}>
                                            <span className={classes.tabRailLine} />
                                            {TABS.map(tab => {
                                                const isActive = activeTab === tab.key;
                                                const TabIcon = tab.icon;
                                                return (
                                                    <button
                                                        key={tab.key}
                                                        type="button"
                                                        className={`${classes.tabButton} ${isActive ? classes.tabButtonActive : ''}`}
                                                        onClick={() => setActiveTab(tab.key)}
                                                    >
                                                        <span className={classes.tabIconWrap} style={{ backgroundColor: tab.bgColor, color: tab.color }}>
                                                            <TabIcon fontSize="small" />
                                                        </span>
                                                        <span className={classes.tabText}>
                                                            <span className={`${classes.tabLabel} ${isActive ? classes.tabLabelActive : ''}`}>
                                                                {tab.label}
                                                            </span>
                                                            <span className={classes.tabDescription}>{tab.description}</span>
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </Box>

                                        <Box className={classes.sideNav}>
                                            <IconButton
                                                size="small"
                                                className={classes.sideNavBtn}
                                                onClick={goToPreviousTab}
                                                disabled={tabIndex === 0}
                                                aria-label="Previous tab"
                                            >
                                                <ChevronLeftIcon fontSize="small" />
                                            </IconButton>
                                            <Typography className={classes.sideNavLabel}>
                                                {tabIndex + 1} of {TABS.length}
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                className={classes.sideNavBtn}
                                                onClick={goToNextTab}
                                                disabled={tabIndex === TABS.length - 1}
                                                aria-label="Next tab"
                                            >
                                                <ChevronRightIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Box>

                                    <Box className={classes.main}>
                                        <FormSpy<TaskflowTask> subscription={{ values: true }}>
                                            {({ values }) => (
                                                <>
                                                    {activeTab === 'details' && (
                                                        <DetailsTab form={form} values={values} taskflow={activeTaskflow} />
                                                    )}
                                                    {activeTab === 'due' && (
                                                        <DueDateTab form={form} values={values} taskflow={activeTaskflow} />
                                                    )}
                                                    {activeTab === 'onCompletion' && (
                                                        <OnCompletionTab form={form} values={values} taskflow={activeTaskflow} />
                                                    )}
                                                    {activeTab === 'access' && (
                                                        <AccessRuleTab form={form} values={values} taskflow={activeTaskflow} />
                                                    )}
                                                </>
                                            )}
                                        </FormSpy>
                                    </Box>
                                </Box>
                            </DialogContent>
                            <DialogActions className={classes.footer}>
                                <Button onClick={saveAndClose} variant="contained" color="primary">Save</Button>
                                <Button onClick={closeAndDiscard}>Cancel</Button>
                            </DialogActions>
                        </Dialog>

                        <ConfirmDialog
                            open={isConfirmDiscardOpen}
                            title={isNewTask ? 'Discard this task?' : 'Discard unsaved changes?'}
                            message={
                                isNewTask
                                    ? 'This task hasn\'t been added to the Taskflow yet. If you close now, it won\'t be created.'
                                    : 'You have changes in this task that haven\'t been kept. If you close now, those changes will be discarded.'
                            }
                            confirmLabel={isNewTask ? 'Discard task' : 'Discard changes'}
                            cancelLabel="Keep editing"
                            onCancel={() => setIsConfirmDiscardOpen(false)}
                            onConfirm={closeAndDiscard}
                        />
                    </>
                );
            }}
        />
    );
};
